import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { SponsorBanner } from '@/components/SponsorBanner';
import LatestNewsPanel from '@/components/LatestNewsPanel';
import MostActiveBriteLabsCard from '@/components/MostActiveBriteLabsCard';
import PeopleToFollowCard from '@/components/PeopleToFollowCard';
import usePageMeta from '@/hooks/usePageMeta';
import { Card } from '@/components/ui/card';
import MemberAvatar from '@/components/MemberAvatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MessageSquare, ThumbsUp, ThumbsDown, Send, Plus, Hash, Clock, Trash2,
  ChevronLeft, ChevronRight, ShieldBan, ShieldCheck, X as XIcon, User,
  Sparkles, ArrowLeft, Heart, FlaskConical, Flag, Pencil, Paperclip, FileText, Image, Download,
  Search, Filter, Calendar, Users, Pin, Link as LinkIcon, Reply, Check, Bookmark
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { MentionInput, RenderMentions } from '@/components/MentionInput';
import MentionRichTextEditor from '@/components/MentionRichTextEditor';
import RichText from '@/components/RichText';

const HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/;

/**
 * Renders a forum body: HTML if the value is rich text (produced by the new
 * TipTap composer), else falls back to the legacy MentionInput's `@[label](id)`
 * parser so existing posts continue to render mentions correctly.
 */
function PostBody({ content, onUserClick, onCompanyClick, className = '' }) {
  if (!content) return null;
  if (HTML_TAG_RE.test(content)) {
    return <RichText html={content} className={className} testId="post-body-html" />;
  }
  return (
    <p className={`whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere] ${className}`} data-testid="post-body-legacy">
      <RenderMentions text={content} onUserClick={onUserClick} onCompanyClick={onCompanyClick} />
    </p>
  );
}
import LinkPreviews from '@/components/LinkPreviews';
import { REACTION_TYPES } from '@/lib/reactions';
import ReportDialog from '@/components/ReportDialog';
import PinUntilDialog from '@/components/PinUntilDialog';
import MultiSelectTopics from '@/components/MultiSelectTopics';
import QuickActions from '@/components/QuickActions';
import { toast } from 'sonner';

const BACKEND_URL = '';

// Reaction vocabulary shared between posts and comments.
const REACTION_TYPES_LEGACY_NOTE = null; // moved to /lib/reactions
void REACTION_TYPES_LEGACY_NOTE;

// Maximum words allowed in a forum post body.
const POST_WORD_LIMIT = 500;

function countWords(text) {
  if (!text) return 0;
  // Strip HTML tags so the count reflects visible words, not markup.
  const plain = String(text).replace(/<[^>]*>/g, ' ');
  return plain.trim().split(/\s+/).filter(Boolean).length;
}

function WordCountLine({ text, limit = POST_WORD_LIMIT, testId }) {
  const count = countWords(text);
  const over = count - limit;
  if (over > 0) {
    return (
      <p className="text-xs mt-1 font-medium text-rose-600" data-testid={testId || 'post-word-count'}>
        {count} / {limit} words · <span className="font-semibold">{over} over the limit</span> — please shorten by {over} word{over === 1 ? '' : 's'} before publishing.
      </p>
    );
  }
  const remaining = limit - count;
  const warn = remaining <= 50;
  return (
    <p className={`text-xs mt-1 ${warn ? 'text-amber-600' : 'text-slate-500'}`} data-testid={testId || 'post-word-count'}>
      {count} / {limit} words {warn && `· ${remaining} left`}
    </p>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function AuthorAvatar({ name, picture, size = 'md' }) {
  // Delegates to the shared MemberAvatar so the brand gradient + black-initial
  // fallback fires correctly when the picture endpoint 404s.
  const px = size === 'sm' ? 28 : size === 'lg' ? 48 : 36;
  return <MemberAvatar name={name} picture={picture} size={px} />;
}

function PostCard({ post, onReact, onOpenPost, onAuthorClick, onMentionUserClick, onMentionCompanyClick, onRequestBriteLab, feedbackPromptId, onFeedback, isAdmin, onTogglePin, onDelete, onToggleSave, currentUserId }) {
  const showFeedback = feedbackPromptId === post.post_id;
  const [showReactions, setShowReactions] = useState(false);

  const REACTIONS = REACTION_TYPES;

  const myReaction = REACTIONS.find(r => r.type === post.my_reaction);
  const reactionCounts = post.reaction_counts || {};
  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0) || post.like_count || 0;

  return (
    <Card className={`p-5 hover:shadow-md transition-shadow ${post.is_pinned ? 'border-2 border-amber-400 bg-gradient-to-br from-amber-50/50 via-white to-amber-50/30 shadow-sm relative' : ''}`} data-testid={`forum-post-${post.post_id}`}>
      {post.is_pinned && (
        <div className="absolute -top-3 left-4 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold uppercase tracking-wider shadow-sm" data-testid={`featured-badge-${post.post_id}`}>
          <Pin className="h-3 w-3" /> Featured Post
        </div>
      )}
      <div className="flex items-start gap-3">
        <button onClick={() => onAuthorClick(post.author_id)} className="flex-shrink-0" data-testid={`post-author-avatar-${post.post_id}`}>
          <AuthorAvatar name={post.author_name} picture={post.author_picture} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => onAuthorClick(post.author_id)} className="font-semibold text-sm text-slate-800 hover:text-brand-navy transition-colors">
              {post.author_name}
            </button>
            {post.author_role === 'admin' && <Badge variant="outline" className="text-[10px] border-brand-navy/30 text-brand-navy px-1.5 py-0">Admin</Badge>}
            {post.is_recommended && (
              <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200 px-1.5 py-0 gap-0.5" data-testid={`recommended-badge-${post.post_id}`}>
                <Sparkles className="h-2.5 w-2.5" /> For You
              </Badge>
            )}
            <span className="text-xs text-slate-400">{timeAgo(post.created_at)}</span>
            {isAdmin && (
              <button onClick={(e) => { e.stopPropagation(); onTogglePin(post); }}
                className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                  post.is_pinned ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
                data-testid={`pin-button-${post.post_id}`}
                title={post.is_pinned ? 'Unpin' : 'Pin to top'}>
                <Pin className="h-3 w-3" /> {post.is_pinned ? 'Pinned' : 'Pin'}
              </button>
            )}
            {(isAdmin || post.author_id === currentUserId) && (
              <button onClick={(e) => { e.stopPropagation(); onDelete(post); }}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border border-slate-200 text-slate-500 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors ${isAdmin && !post.is_pinned ? '' : 'ml-auto'}`}
                data-testid={`card-delete-button-${post.post_id}`}
                title="Delete post">
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>

          {post.title && (
            <button onClick={() => onOpenPost(post.post_id)} className="block text-left mt-1 w-full min-w-0" data-testid={`post-title-${post.post_id}`}>
              <h3 className="font-bold text-slate-900 hover:text-brand-navy transition-colors leading-snug break-words [overflow-wrap:anywhere]">{post.title}</h3>
            </button>
          )}
          {/* Body text — also opens the post detail dialog on click, matching the title behaviour. */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => onOpenPost(post.post_id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenPost(post.post_id); } }}
            className="cursor-pointer"
            data-testid={`post-body-${post.post_id}`}
          >
            <div className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-4 hover:text-slate-800 transition-colors">
              <PostBody content={post.content} onUserClick={onMentionUserClick} onCompanyClick={onMentionCompanyClick} className="compact card card-4" />
            </div>
          </div>

          {/* Image preview on card — single image gets hero treatment; multiple use compact thumbnail row */}
          {post.attachments?.filter(a => a.is_image).length > 0 && (() => {
            const imgs = post.attachments.filter(a => a.is_image);
            if (imgs.length === 1) {
              return (
                <div className="mt-3 -mx-0.5">
                  <img src={`${BACKEND_URL}${imgs[0].url}`} alt=""
                    className="w-full max-h-80 object-cover rounded-lg border border-slate-200" />
                </div>
              );
            }
            return (
              <div className={`mt-3 grid gap-1.5 ${imgs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {imgs.slice(0, 6).map((att, i) => (
                  <div key={att.file_id} className="relative">
                    <img src={`${BACKEND_URL}${att.url}`} alt=""
                      className="w-full h-32 object-cover rounded-lg border border-slate-200" />
                    {i === 5 && imgs.length > 6 && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center text-white font-semibold">
                        +{imgs.length - 6}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          {post.attachments?.filter(a => !a.is_image).length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
              <Paperclip className="h-3 w-3" />
              <span>{post.attachments.filter(a => !a.is_image).length} file(s) attached</span>
            </div>
          )}

          {/* Link preview card */}
          {post.link_preview?.url && (
            <a href={post.link_preview.url} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="block mt-3 border border-slate-200 rounded-lg overflow-hidden hover:border-brand-navy/40 hover:shadow-sm transition-all"
              data-testid={`post-link-${post.post_id}`}>
              <div className="flex items-stretch">
                {post.link_preview.image && (
                  <img src={post.link_preview.image.startsWith('/') ? `${BACKEND_URL}${post.link_preview.image}` : post.link_preview.image}
                    alt="" className="w-28 h-20 object-cover flex-shrink-0 bg-slate-100" />
                )}
                <div className="p-2.5 flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{post.link_preview.site}</p>
                  <p className="text-sm font-semibold text-slate-800 line-clamp-2">{post.link_preview.title || post.link_preview.url}</p>
                  {post.link_preview.description && <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{post.link_preview.description}</p>}
                </div>
              </div>
            </a>
          )}

          {post.topics?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {post.topics.map(t => (
                <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-navy/8 text-brand-navy/80 font-medium">#{t}</span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-3 pt-2 border-t border-slate-100">
            <div className="relative" onMouseEnter={() => setShowReactions(true)} onMouseLeave={() => setShowReactions(false)}>
              <button data-testid={`react-btn-${post.post_id}`}
                onClick={() => onReact(post.post_id, myReaction ? myReaction.type : 'like')}
                className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${myReaction ? 'text-brand-navy' : 'text-slate-400 hover:text-brand-navy'}`}>
                <span className="text-base leading-none">{myReaction ? myReaction.emoji : '\uD83D\uDC4D'}</span>
                {totalReactions > 0 && <span>{totalReactions}</span>}
                {!myReaction && <span>Like</span>}
              </button>
              {showReactions && (
                <div className="absolute -top-11 left-0 flex items-center gap-0.5 bg-white shadow-lg rounded-full px-2 py-1.5 border z-50" data-testid={`reaction-picker-${post.post_id}`}>
                  {REACTIONS.map(r => (
                    <button key={r.type} onClick={(e) => { e.stopPropagation(); onReact(post.post_id, r.type); setShowReactions(false); }}
                      title={r.label} data-testid={`reaction-${r.type}-${post.post_id}`}
                      className={`text-xl hover:scale-125 transition-transform px-0.5 ${post.my_reaction === r.type ? 'scale-125' : ''}`}>
                      {r.emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {Object.entries(reactionCounts).filter(([,c]) => c > 0).length > 0 && (
              <div className="flex items-center gap-0.5 text-xs text-slate-400">
                {Object.entries(reactionCounts).filter(([,c]) => c > 0).map(([type]) => {
                  const r = REACTIONS.find(rx => rx.type === type);
                  return r ? <span key={type} className="text-sm">{r.emoji}</span> : null;
                })}
              </div>
            )}
            <button onClick={() => onOpenPost(post.post_id)} data-testid={`comment-btn-${post.post_id}`}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-brand-navy font-medium transition-colors">
              <MessageSquare className="h-4 w-4" />
              {post.comment_count || 0}
            </button>
            <button onClick={(e) => { e.stopPropagation(); onToggleSave(post); }}
              data-testid={`save-btn-${post.post_id}`}
              title={post.saved_by_me ? 'Remove from saved' : 'Save for later'}
              className={`ml-auto flex items-center gap-1.5 text-xs font-medium transition-colors ${
                post.saved_by_me ? 'text-amber-600' : 'text-slate-400 hover:text-amber-600'
              }`}>
              <Bookmark className={`h-4 w-4 ${post.saved_by_me ? 'fill-amber-500' : ''}`} />
              {post.saved_by_me ? 'Saved' : 'Save'}
            </button>
          </div>

          {showFeedback && (
            <div className="mt-3 p-3 bg-brand-navy/5 rounded-lg flex items-center gap-3 animate-in slide-in-from-bottom-2" data-testid="feedback-prompt">
              <Sparkles className="h-4 w-4 text-brand-navy flex-shrink-0" />
              <span className="text-xs text-slate-600 flex-1">Did you find this post useful?</span>
              <button onClick={() => onFeedback(post.post_id, true)} data-testid="feedback-yes"
                className="p-1.5 rounded-md hover:bg-green-100 text-green-600 transition-colors"><ThumbsUp className="h-4 w-4" /></button>
              <button onClick={() => onFeedback(post.post_id, false)} data-testid="feedback-no"
                className="p-1.5 rounded-md hover:bg-red-100 text-red-400 transition-colors"><ThumbsDown className="h-4 w-4" /></button>
            </div>
          )}
        </div>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid={`request-britelab-${post.post_id}`}
                onClick={(e) => { e.stopPropagation(); onRequestBriteLab(post); }}
                className="flex-shrink-0 p-2 rounded-lg text-slate-300 hover:text-brand-navy hover:bg-brand-navy/5 transition-all self-start mt-1"
              >
                <FlaskConical className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Request BriteLab</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </Card>
  );
}

export default function ForumPage({ user, isArchive = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTopicParam = searchParams.get('topic') || '';
  const viewUserParam = searchParams.get('user') || '';
  const postParam = searchParams.get('post') || '';

  // How many pages of the "live" main feed we show before older posts get
  // routed into /forum/archive.
  const MAIN_FEED_PAGE_CAP = 3;

  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [activeTopic, setActiveTopic] = useState(activeTopicParam);
  const [showCreate, setShowCreate] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', topics: [] });
  const [creating, setCreating] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPreview, setLinkPreview] = useState(null);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [customLinkImage, setCustomLinkImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Post detail
  const [selectedPost, setSelectedPost] = useState(null);
  // Per-post browser title + share meta so a forum post link unfurls with
  // its own title rather than the generic site name.
  usePageMeta(selectedPost ? {
    title: selectedPost.title || 'Forum post',
    description: (selectedPost.body || selectedPost.content || '').slice(0, 200),
  } : { title: 'Forum' });
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [openCommentReactions, setOpenCommentReactions] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingComment, setUploadingComment] = useState(false);
  const commentImageInputRef = useRef(null);

  // Breakout Groups
  const [breakoutGroups, setBreakoutGroups] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupForm, setNewGroupForm] = useState({ name: '', description: '' });
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupMsgText, setGroupMsgText] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [detailTab, setDetailTab] = useState('comments');

  // User posts view
  const [viewUser, setViewUser] = useState(null);
  const [viewUserPosts, setViewUserPosts] = useState([]);

  // Blocked users
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [showBlocked, setShowBlocked] = useState(false);

  // AI feedback
  const [feedbackPromptId, setFeedbackPromptId] = useState(null);
  const [readCount, setReadCount] = useState(0);
  const [forYou, setForYou] = useState(false);
  // "Save for later" — when toggled, the feed switches to the user's
  // bookmarked posts (most-recent-save first) so they can come back to
  // anything they flagged from the firehose. Lives client-side so the
  // user can flip back to All Posts without re-fetching the feed.
  const [savedMode, setSavedMode] = useState(false);
  const [savedPosts, setSavedPosts] = useState([]);
  // In-feed banners — fetched once on mount and injected after every
  // FEED_BANNER_INTERVAL posts. Rotates through available banners so
  // we don't show the same one repeatedly on a long feed.
  const [feedBanners, setFeedBanners] = useState([]);
  const [forumSearch, setForumSearch] = useState('');
  // Mobile detection — shorter placeholder fits inside the narrow search box.
  const [isMobileForum, setIsMobileForum] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobileForum(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const [forumAuthor, setForumAuthor] = useState('');
  const [forumDateFrom, setForumDateFrom] = useState('');
  const [forumDateTo, setForumDateTo] = useState('');
  const [showForumFilters, setShowForumFilters] = useState(false);
  const [timeline, setTimeline] = useState('all');

  // Report member
  const [reportTarget, setReportTarget] = useState(null);
  const [pinTarget, setPinTarget] = useState(null);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const fetchOpts = { credentials: 'include', headers };

  const loadTopics = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/topics`, fetchOpts);
      if (res.ok) { const d = await res.json(); setTopics(d.topics || []); }
    } catch {}
  }, []);

  const loadFeed = useCallback(async (p = 1, topic = '', forYouMode = false) => {
    setLoading(true);
    try {
      // Archive route requests continue past the first MAIN_FEED_PAGE_CAP
      // pages by offsetting the backend page number. From the user's POV
      // they see Archive page 1..N; behind the scenes we ask the backend
      // for page (N + MAIN_FEED_PAGE_CAP).
      const backendPage = isArchive ? p + MAIN_FEED_PAGE_CAP : p;
      const params = new URLSearchParams({ page: backendPage, limit: 10 });
      if (topic) params.set('topic', topic);
      if (forYouMode) params.set('for_you', 'true');
      if (forumSearch) params.set('search', forumSearch);
      if (forumAuthor) params.set('author_name', forumAuthor);
      if (forumDateFrom) params.set('date_from', forumDateFrom);
      if (forumDateTo) params.set('date_to', forumDateTo);
      if (timeline && timeline !== 'all') params.set('timeline', timeline);
      const res = await fetch(`${BACKEND_URL}/api/forum/feed?${params}`, fetchOpts);
      if (res.ok) {
        const d = await res.json();
        setPosts(d.posts || []);
        const backendPages = d.pages || 1;
        setTotalPosts(d.total || 0);
        if (isArchive) {
          // Archive shows only what's past the main-feed cap
          setTotalPages(Math.max(0, backendPages - MAIN_FEED_PAGE_CAP));
          setPage(p);
        } else {
          setTotalPages(Math.min(backendPages, MAIN_FEED_PAGE_CAP));
          setPage(d.page || 1);
        }
      }
    } catch { toast.error('Failed to load feed'); }
    setLoading(false);
  }, [isArchive]);

  const loadBlockedUsers = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/blocked`, fetchOpts);
      if (res.ok) setBlockedUsers(await res.json());
    } catch {}
  }, []);

  // Pull the user's saved posts (most-recent-save first) — used to power
  // the Saved tab AND to keep the bookmark icons in the main feed in sync
  // when the user toggles save away from the saved view.
  const loadSavedPosts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/saved?limit=100`, fetchOpts);
      if (res.ok) {
        const d = await res.json();
        setSavedPosts(d.posts || []);
      }
    } catch {}
  }, []);

  const handleToggleSave = async (post) => {
    // Optimistic flip — we'll roll back on failure. Updates both the
    // main feed and the saved-posts list so the UI stays consistent.
    const nextSaved = !post.saved_by_me;
    const apply = (list) => list.map(p => p.post_id === post.post_id ? { ...p, saved_by_me: nextSaved } : p);
    setPosts(prev => apply(prev));
    setViewUserPosts(prev => apply(prev));
    if (selectedPost?.post_id === post.post_id) {
      setSelectedPost(p => ({ ...p, saved_by_me: nextSaved }));
    }
    if (nextSaved) {
      setSavedPosts(prev => prev.find(p => p.post_id === post.post_id)
        ? apply(prev)
        : [{ ...post, saved_by_me: true }, ...prev]);
    } else {
      setSavedPosts(prev => prev.filter(p => p.post_id !== post.post_id));
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${post.post_id}/save`, {
        ...fetchOpts, method: 'POST',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(nextSaved ? 'Saved for later' : 'Removed from saved');
    } catch {
      // Roll back on failure
      const rollback = (list) => list.map(p => p.post_id === post.post_id ? { ...p, saved_by_me: !nextSaved } : p);
      setPosts(prev => rollback(prev));
      setViewUserPosts(prev => rollback(prev));
      if (selectedPost?.post_id === post.post_id) {
        setSelectedPost(p => ({ ...p, saved_by_me: !nextSaved }));
      }
      // Re-pull saved list to ensure consistency
      loadSavedPosts();
      toast.error('Could not update saved status');
    }
  };

  useEffect(() => { loadTopics(); loadBlockedUsers(); loadSavedPosts(); }, []);

  // Pull the available in-feed sponsor banners once on mount. Any banner
  // uploaded with placement="forum-inline" enters the rotation, regardless
  // of banner_type — the admin can pick whichever aspect ratio fits.
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/banners?placement=forum-inline`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const feeds = Array.isArray(list) ? list : [];
        setFeedBanners(feeds);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { if (!viewUserParam) loadFeed(1, activeTopic, forYou); }, [activeTopic, viewUserParam, forYou, forumSearch, forumAuthor, forumDateFrom, forumDateTo, timeline]);

  useEffect(() => {
    if (viewUserParam) {
      loadUserPosts(viewUserParam);
    } else {
      setViewUser(null);
      setViewUserPosts([]);
    }
  }, [viewUserParam]);

  // Deep-link: when arriving from a notification email/bell with ?post=<id>,
  // auto-open the post detail dialog. Runs only when the param changes so it
  // doesn't fight the user's own dialog-close action.
  useEffect(() => {
    if (postParam) {
      handleOpenPost(postParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postParam]);

  const loadUserPosts = async (userId) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/user/${userId}/posts`, fetchOpts);
      if (res.ok) {
        const d = await res.json();
        setViewUser(d.user);
        setViewUserPosts(d.posts || []);
      } else {
        toast.error('Cannot view this user\'s posts');
        setSearchParams({});
      }
    } catch { toast.error('Failed to load user posts'); }
    setLoading(false);
  };

  const handleReact = async (postId, reactionType) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/react`, {
        ...fetchOpts, method: 'POST',
        body: JSON.stringify({ reaction: reactionType })
      });
      if (res.ok) {
        const { reactions, my_reaction, total } = await res.json();
        const update = p => p.post_id === postId ? { ...p, reaction_counts: reactions, my_reaction, like_count: total } : p;
        setPosts(prev => prev.map(update));
        setViewUserPosts(prev => prev.map(update));
        if (selectedPost?.post_id === postId) setSelectedPost(p => ({ ...p, reaction_counts: reactions, my_reaction, like_count: total }));
      }
    } catch {}
  };

  const handleOpenPost = async (postId) => {
    setDetailLoading(true);
    setSelectedPost(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}`, fetchOpts);
      if (res.ok) {
        const postData = await res.json();
        setSelectedPost(postData);
        loadBreakoutGroups(postId);
        setActiveGroup(null);
        setGroupMessages([]);
      }
      else toast.error('Post not available');
    } catch { toast.error('Failed to load post'); }
    setDetailLoading(false);

    // AI feedback trigger: every 3 posts viewed
    setReadCount(c => {
      const next = c + 1;
      if (next % 3 === 0) setFeedbackPromptId(postId);
      return next;
    });
  };

  const handleComment = async () => {
    if ((!commentText.trim() && commentAttachments.length === 0) || !selectedPost) return;
    setSubmittingComment(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedPost.post_id}/comments`, {
        ...fetchOpts, method: 'POST',
        body: JSON.stringify({
          content: commentText.trim(),
          attachments: commentAttachments.map(a => ({
            file_id: a.file_id, filename: a.filename, content_type: a.content_type,
            size: a.size, is_image: a.is_image, url: a.url,
          })),
        }),
      });
      if (res.ok) {
        const comment = await res.json();
        setSelectedPost(p => ({ ...p, comments: [...(p.comments || []), comment], comment_count: (p.comment_count || 0) + 1 }));
        const update = p => p.post_id === selectedPost.post_id ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p;
        setPosts(prev => prev.map(update));
        setViewUserPosts(prev => prev.map(update));
        setCommentText('');
        setCommentAttachments([]);
      }
    } catch { toast.error('Failed to add comment'); }
    setSubmittingComment(false);
  };

  const handleCommentImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingComment(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 1 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 1MB)`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${BACKEND_URL}/api/forum/upload`, { method: 'POST', credentials: 'include', body: formData });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Upload failed: ${file.name}`);
          continue;
        }
        uploaded.push(await res.json());
      }
      setCommentAttachments(prev => [...prev, ...uploaded]);
      if (uploaded.length) toast.success(`${uploaded.length} image(s) attached`);
    } catch { toast.error('Image upload failed'); }
    setUploadingComment(false);
    if (commentImageInputRef.current) commentImageInputRef.current.value = '';
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedPost) return;
    try {
      await fetch(`${BACKEND_URL}/api/forum/comments/${commentId}`, { ...fetchOpts, method: 'DELETE' });
      setSelectedPost(p => ({
        ...p, comments: p.comments.filter(c => c.comment_id !== commentId),
        comment_count: Math.max(0, (p.comment_count || 1) - 1)
      }));
      const update = p => p.post_id === selectedPost.post_id ? { ...p, comment_count: Math.max(0, (p.comment_count || 1) - 1) } : p;
      setPosts(prev => prev.map(update));
    } catch {}
  };

  const startEditComment = (c) => { setEditingCommentId(c.comment_id); setEditingCommentText(c.content); };

  const saveEditComment = async (commentId) => {
    const text = editingCommentText.trim();
    if (!text) { toast.error('Comment cannot be empty'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/comments/${commentId}`, {
        ...fetchOpts, method: 'PUT', body: JSON.stringify({ content: text })
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setSelectedPost(p => ({
        ...p,
        comments: p.comments.map(c => c.comment_id === commentId ? { ...c, ...updated } : c),
      }));
      setEditingCommentId(null);
      setEditingCommentText('');
      toast.success('Comment updated');
    } catch { toast.error('Failed to save comment'); }
  };

  const startReplyToComment = (c) => {
    setReplyToCommentId(c.comment_id);
    // Pre-fill with an @-mention of the parent commenter so the reply
    // will always tag them — the backend counts a tag as a stronger
    // signal (email + in-app notif) and this saves the user having to
    // remember to type "@Name" themselves.
    const uid = c.author_id;
    const name = c.author_name || 'Member';
    setReplyText(uid
      ? `<p><a href="/profile/public/${uid}" class="mention" data-mention-id="${uid}" data-id="${uid}" data-label="${name}">@${name}</a>&nbsp;</p>`
      : '');
  };

  const submitReply = async (parentCommentId) => {
    const text = replyText.trim();
    if (!text || !selectedPost) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedPost.post_id}/comments`, {
        ...fetchOpts, method: 'POST',
        body: JSON.stringify({ content: text, parent_comment_id: parentCommentId, attachments: [] })
      });
      if (!res.ok) throw new Error();
      const newComment = await res.json();
      setSelectedPost(p => ({
        ...p,
        comments: [...p.comments, { ...newComment, author_name: user.name, author_picture: user.picture, author_role: user.role, reactions: {}, my_reaction: null }],
        comment_count: (p.comment_count || 0) + 1,
      }));
      setReplyToCommentId(null);
      setReplyText('');
    } catch { toast.error('Failed to reply'); }
  };

  const reactToComment = async (commentId, reactionType) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/comments/${commentId}/react`, {
        ...fetchOpts, method: 'POST', body: JSON.stringify({ reaction: reactionType })
      });
      if (!res.ok) throw new Error();
      const { reactions, my_reaction } = await res.json();
      setSelectedPost(p => ({
        ...p,
        comments: p.comments.map(c => c.comment_id === commentId ? { ...c, reactions, my_reaction } : c),
      }));
      setOpenCommentReactions(null);
    } catch { toast.error('Failed to react'); }
  };

  const sharePostLink = async (postId) => {
    const url = `${window.location.origin}/forum?post=${postId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Post link copied — paste it into a message or anywhere else.');
    } catch {
      window.prompt('Copy this link to share the post:', url);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!(await window.appConfirm('Delete this post?'))) return;
    try {
      await fetch(`${BACKEND_URL}/api/forum/posts/${postId}`, { ...fetchOpts, method: 'DELETE' });
      toast.success('Post deleted');
      setSelectedPost(null);
      setPosts(prev => prev.filter(p => p.post_id !== postId));
      setViewUserPosts(prev => prev.filter(p => p.post_id !== postId));
    } catch { toast.error('Failed to delete'); }
  };

  const handleCardDeletePost = (post) => handleDeletePost(post.post_id);

  const handleTogglePin = async (post) => {
    if (post.is_pinned) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/forum/posts/${post.post_id}/pin`, {
          ...fetchOpts, method: 'POST', body: JSON.stringify({ clear: true }),
        });
        if (!res.ok) throw new Error();
        toast.success('Unpinned');
        loadFeed(page, activeTopic, forYou);
      } catch { toast.error('Failed to unpin'); }
      return;
    }
    setPinTarget(post);
  };

  const submitPin = async (untilIso) => {
    if (!pinTarget) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${pinTarget.post_id}/pin`, {
        ...fetchOpts, method: 'POST', body: JSON.stringify({ until: untilIso }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      toast.success('Post pinned to top');
      setPinTarget(null);
      loadFeed(page, activeTopic, forYou);
    } catch (e) { toast.error(e.message || 'Failed to pin'); }
  };

  const [editingPost, setEditingPost] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });

  const handleEditPost = async () => {
    if (!editForm.content.trim()) { toast.error('Content is required'); return; }
    const wc = countWords(editForm.content);
    if (wc > POST_WORD_LIMIT) {
      toast.error(`Your post is ${wc - POST_WORD_LIMIT} word${wc - POST_WORD_LIMIT === 1 ? '' : 's'} over the ${POST_WORD_LIMIT}-word limit. Please shorten it before saving.`);
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${editingPost.post_id}`, {
        ...fetchOpts, method: 'PUT', body: JSON.stringify(editForm),
      });
      if (res.ok) {
        const updated = await res.json();
        setPosts(prev => prev.map(p => p.post_id === updated.post_id ? { ...p, ...updated } : p));
        if (selectedPost?.post_id === updated.post_id) setSelectedPost(prev => ({ ...prev, ...updated }));
        setEditingPost(null);
        toast.success('Post updated');
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to update'); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      // Images are capped at 1MB (forum cards only need ~1200px wide);
      // PDFs/docs/decks can go up to 10MB.
      const isImage = (file.type || '').startsWith('image/');
      const cap = isImage ? 1 * 1024 * 1024 : 10 * 1024 * 1024;
      const capLabel = isImage ? '1MB' : '10MB';
      if (file.size > cap) { toast.error(`${file.name} is too large (max ${capLabel})`); continue; }
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${BACKEND_URL}/api/forum/upload`, { method: 'POST', credentials: 'include', body: formData });
        if (res.ok) {
          const data = await res.json();
          setAttachments(prev => [...prev, data]);
        } else { toast.error(`Failed to upload ${file.name}`); }
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) { toast.error('Post content is required'); return; }
    if (newPost.topics.length === 0) { toast.error('Select at least one topic'); return; }
    const wc = countWords(newPost.content);
    if (wc > POST_WORD_LIMIT) {
      toast.error(`Your post is ${wc - POST_WORD_LIMIT} word${wc - POST_WORD_LIMIT === 1 ? '' : 's'} over the ${POST_WORD_LIMIT}-word limit. Please shorten it before publishing.`);
      return;
    }
    setCreating(true);
    try {
      const finalLinkPreview = linkPreview ? { ...linkPreview, image: customLinkImage?.url || linkPreview.image } : null;
      const payload = {
        ...newPost,
        attachments: attachments.map(a => ({ file_id: a.file_id, filename: a.filename, content_type: a.content_type, size: a.size, is_image: a.is_image, url: a.url })),
        link_preview: finalLinkPreview,
      };
      const res = await fetch(`${BACKEND_URL}/api/forum/posts`, {
        ...fetchOpts, method: 'POST', body: JSON.stringify(payload),
      });
      if (res.ok) {
        const post = await res.json();
        setPosts(prev => [post, ...prev]);
        setNewPost({ title: '', content: '', topics: [] });
        setAttachments([]);
        setLinkUrl(''); setLinkPreview(null); setCustomLinkImage(null);
        setShowCreate(false);
        toast.success('Post published!');
        loadTopics();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create post');
      }
    } catch { toast.error('Failed to create post'); }
    setCreating(false);
  };

  const fetchLinkPreview = async () => {
    const url = linkUrl.trim();
    if (!url) return;
    setFetchingPreview(true);
    setLinkPreview(null);
    setCustomLinkImage(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/url-preview?url=${encodeURIComponent(url)}`, { ...fetchOpts });
      if (!res.ok) throw new Error();
      const meta = await res.json();
      setLinkPreview(meta);
    } catch {
      toast.error("Couldn't fetch that URL — you can still attach an image manually");
      setLinkPreview({ title: '', description: '', image: '', url, site: '' });
    } finally {
      setFetchingPreview(false);
    }
  };

  const handleLinkImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/upload`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCustomLinkImage(data);
      if (!linkPreview) setLinkPreview({ title: '', description: '', image: data.url, url: linkUrl, site: '' });
      toast.success('Custom image attached to link');
    } catch { toast.error('Image upload failed'); }
  };

  const handleBlock = async (userId, name) => {
    if (!(await window.appConfirm(`Block ${name}? Their posts will be hidden from your feed.`))) return;
    try {
      await fetch(`${BACKEND_URL}/api/forum/block/${userId}`, { ...fetchOpts, method: 'POST' });
      toast.success(`${name} blocked`);
      setPosts(prev => prev.filter(p => p.author_id !== userId));
      setSelectedPost(null);
      loadBlockedUsers();
    } catch { toast.error('Failed to block user'); }
  };

  const handleUnblock = async (userId) => {
    try {
      await fetch(`${BACKEND_URL}/api/forum/block/${userId}`, { ...fetchOpts, method: 'DELETE' });
      toast.success('User unblocked');
      loadBlockedUsers();
      loadFeed(page, activeTopic);
    } catch { toast.error('Failed to unblock'); }
  };

  const handleFeedback = async (postId, useful) => {
    try {
      await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/feedback`, {
        ...fetchOpts, method: 'POST', body: JSON.stringify({ useful }),
      });
      toast.success(useful ? 'Thanks! We\'ll show more like this' : 'Got it, we\'ll adjust your feed');
      setFeedbackPromptId(null);
    } catch {}
  };

  // Breakout Group handlers
  const loadBreakoutGroups = async (postId) => {
    setLoadingGroups(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/breakout-groups`, fetchOpts);
      if (res.ok) setBreakoutGroups(await res.json());
    } catch {} finally { setLoadingGroups(false); }
  };

  const handleCreateGroup = async () => {
    if (!newGroupForm.name.trim() || !selectedPost) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedPost.post_id}/breakout-groups`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGroupForm),
      });
      if (res.ok) {
        toast.success('Breakout group created');
        setShowCreateGroup(false);
        setNewGroupForm({ name: '', description: '' });
        loadBreakoutGroups(selectedPost.post_id);
      }
    } catch { toast.error('Failed to create group'); }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await fetch(`${BACKEND_URL}/api/forum/breakout-groups/${groupId}/join`, { method: 'POST', credentials: 'include' });
      if (selectedPost) loadBreakoutGroups(selectedPost.post_id);
    } catch {}
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await fetch(`${BACKEND_URL}/api/forum/breakout-groups/${groupId}/leave`, { method: 'POST', credentials: 'include' });
      if (activeGroup?.group_id === groupId) { setActiveGroup(null); setGroupMessages([]); }
      if (selectedPost) loadBreakoutGroups(selectedPost.post_id);
    } catch {}
  };

  const handleRenameGroup = async (groupId) => {
    const current = activeGroup?.group_id === groupId
      ? activeGroup.name
      : (breakoutGroups.find(g => g.group_id === groupId)?.name || '');
    const newName = window.prompt('Rename breakout group', current);
    if (newName === null) return;
    const trimmed = newName.trim();
    if (!trimmed || trimmed === current) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/breakout-groups/${groupId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to rename');
      }
      const updated = await res.json();
      if (activeGroup?.group_id === groupId) setActiveGroup(prev => ({ ...prev, name: updated.name }));
      if (selectedPost) loadBreakoutGroups(selectedPost.post_id);
      toast.success('Group renamed');
    } catch (e) {
      toast.error(e.message || 'Failed to rename');
    }
  };

  const openGroupChat = async (group) => {
    setActiveGroup(group);
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/breakout-groups/${group.group_id}/messages`, fetchOpts);
      if (res.ok) setGroupMessages(await res.json());
    } catch {}
  };

  const handleSendGroupMsg = async () => {
    if (!groupMsgText.trim() || !activeGroup) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/breakout-groups/${activeGroup.group_id}/messages`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: groupMsgText }),
      });
      if (res.ok) {
        const msg = await res.json();
        setGroupMessages(prev => [...prev, msg]);
        setGroupMsgText('');
      }
    } catch {}
  };


  const handleTopicClick = (topicName) => {
    const next = activeTopic === topicName ? '' : topicName;
    setActiveTopic(next);
    setForYou(false);
    setSearchParams(next ? { topic: next } : {});
  };

  const handleAuthorClick = (userId) => {
    if (!userId) return;
    if (userId === user?.user_id) { navigate('/profile'); return; }
    navigate(`/profile/public/${userId}`);
  };

  const handleMentionUserClick = (userId) => {
    if (!userId) return;
    setSelectedPost(null);
    if (userId === user?.user_id) { navigate('/profile'); return; }
    navigate(`/profile/public/${userId}`);
  };

  const handleMentionCompanyClick = (companyId) => {
    setSelectedPost(null);
    navigate(`/companies/${companyId}`);
  };

  const handleRequestBriteLab = (post) => {
    const title = post.title || '';
    const problem = post.content || '';
    const params = new URLSearchParams({ request: 'true' });
    if (title) params.set('title', title);
    if (problem) params.set('problem', problem.substring(0, 500));
    navigate(`/britelabs?${params.toString()}`);
  };

  const toggleNewPostTopic = (t) => {
    setNewPost(prev => ({
      ...prev,
      topics: prev.topics.includes(t) ? prev.topics.filter(x => x !== t) : prev.topics.length < 5 ? [...prev.topics, t] : prev.topics,
    }));
  };

  const displayPosts = viewUserParam
    ? viewUserPosts
    : savedMode
      ? savedPosts
      : posts;
  const isBlocked = (userId) => blockedUsers.some(b => b.blocked_user_id === userId);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="forum" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">

            {/* Main Feed */}
            <div className="flex-1 min-w-0">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                {viewUserParam && viewUser ? (
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => setSearchParams({})} data-testid="back-to-feed">
                      <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <AuthorAvatar name={viewUser.name} picture={viewUser.picture} size="lg" />
                    <div>
                      <h1 className="text-xl font-bold text-slate-800" data-testid="user-posts-title">{viewUser.name}'s Posts</h1>
                      <p className="text-sm text-slate-500">{viewUserPosts.length} posts</p>
                    </div>
                    {viewUser.user_id !== user?.user_id && (
                      <Button variant="ghost" size="sm" className="text-red-500 ml-2"
                        onClick={() => handleBlock(viewUser.user_id, viewUser.name)} data-testid="block-user-btn">
                        <ShieldBan className="h-4 w-4 mr-1" /> Block
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <div>
                      <h1 className="text-2xl font-bold font-heading gradient-text flex items-center gap-2" data-testid="forum-title">
                        {isArchive ? 'Forum Archive' : 'Community Forum'} <PageExplainer pageKey="forum" label="About the Forum" />
                      </h1>
                      {isArchive && (
                        <p className="text-sm text-slate-500 mt-0.5">Older posts beyond the last 30. Search + filter still work.</p>
                      )}
                      {!isArchive && activeTopic && <p className="text-sm text-slate-500 mt-0.5">Filtering: <span className="text-brand-navy font-medium">#{activeTopic}</span></p>}
                    </div>
                    <Button onClick={() => setShowCreate(true)} data-testid="create-post-btn"
                      className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                      <Plus className="h-4 w-4 mr-1.5" /> New Post
                    </Button>
                  </>
                )}
              </div>

              {/* Feed Mode Tabs */}
              {!viewUserParam && (
                <div className="flex gap-1 mb-4 border-b" data-testid="feed-tabs">
                  <button onClick={() => { setForYou(false); setSavedMode(false); setActiveTopic(''); setSearchParams({}); }}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      !forYou && !savedMode && !activeTopic
                        ? 'border-brand-navy text-brand-navy'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`} data-testid="tab-all-posts">
                    All Posts
                  </button>
                  <button onClick={() => { setForYou(true); setSavedMode(false); setActiveTopic(''); setSearchParams({}); }}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                      forYou
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`} data-testid="tab-for-you">
                    <Sparkles className="h-3.5 w-3.5" /> For You
                  </button>
                  <button onClick={() => { setSavedMode(true); setForYou(false); setActiveTopic(''); setSearchParams({}); loadSavedPosts(); }}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                      savedMode
                        ? 'border-amber-600 text-amber-700'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`} data-testid="tab-saved">
                    <Bookmark className={`h-3.5 w-3.5 ${savedMode ? 'fill-amber-500' : ''}`} /> Saved
                    {savedPosts.length > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full h-4 min-w-[1rem] px-1">
                        {savedPosts.length}
                      </span>
                    )}
                  </button>
                  {activeTopic && !forYou && !savedMode && (
                    <span className="px-3 py-2 text-sm font-medium border-b-2 border-brand-navy text-brand-navy">
                      #{activeTopic}
                    </span>
                  )}
                </div>
              )}

              {/* Search & Filters */}
              {!viewUserParam && (
                <div className="mb-4 space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input placeholder={isMobileForum ? 'Search posts' : 'Search posts by title or content...'}
                        value={forumSearch} onChange={e => setForumSearch(e.target.value)}
                        className="pl-10 h-9" data-testid="forum-search" />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowForumFilters(!showForumFilters)}
                      className={showForumFilters ? 'bg-brand-navy text-white' : ''} data-testid="forum-toggle-filters">
                      <Filter className="h-3.5 w-3.5 mr-1" /> Filters
                      {(forumAuthor || forumDateFrom || forumDateTo) && (
                        <span className="ml-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                          {[forumAuthor, forumDateFrom, forumDateTo].filter(Boolean).length}
                        </span>
                      )}
                    </Button>
                  </div>

                  {/* Timeline Filter */}
                  <div className="flex items-center gap-2 flex-wrap" data-testid="forum-timeline-filter">
                    <div className="flex-1 min-w-[220px] max-w-md">
                      <MultiSelectTopics
                        topics={topics}
                        selected={activeTopic ? activeTopic.split(',').filter(Boolean) : []}
                        onChange={(next) => {
                          const joined = next.join(',');
                          setActiveTopic(joined);
                          setSearchParams(joined ? { topic: joined } : {});
                        }}
                        max={10}
                        placeholder="Filter by topics…"
                        testId="topic-filter"
                      />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {[
                        { value: '30d', label: 'Last 30 days' },
                        { value: '90d', label: 'Last 90 days' },
                        { value: '12m', label: 'Last 12 months' },
                        { value: 'all', label: 'All time' },
                      ].map(t => (
                        <button key={t.value} data-testid={`timeline-${t.value}`}
                          onClick={() => setTimeline(t.value)}
                          className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
                            timeline === t.value
                              ? 'bg-brand-navy text-white'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {showForumFilters && (
                    <div className="p-3 bg-slate-50 border rounded-lg" data-testid="forum-filters-panel">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-slate-600 mb-1 block">Author</Label>
                          <Input placeholder="e.g. Kelly" value={forumAuthor}
                            onChange={e => setForumAuthor(e.target.value)}
                            className="h-8 text-sm" data-testid="forum-filter-author" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600 mb-1 block">Date From</Label>
                          <Input type="date" value={forumDateFrom}
                            onChange={e => setForumDateFrom(e.target.value)}
                            className="h-8 text-sm" data-testid="forum-filter-date-from" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600 mb-1 block">Date To</Label>
                          <Input type="date" value={forumDateTo}
                            onChange={e => setForumDateTo(e.target.value)}
                            className="h-8 text-sm" data-testid="forum-filter-date-to" />
                        </div>
                      </div>
                      {(forumAuthor || forumDateFrom || forumDateTo) && (
                        <Button variant="ghost" size="sm" className="mt-2 text-xs text-red-500" data-testid="forum-clear-filters"
                          onClick={() => { setForumAuthor(''); setForumDateFrom(''); setForumDateTo(''); }}>
                          <XIcon className="h-3 w-3 mr-1" /> Clear filters
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Posts Feed */}
              {loading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
                </div>
              ) : displayPosts.length === 0 ? (
                <Card className="p-12 text-center">
                  <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">
                    {savedMode
                      ? "You haven't saved any posts yet. Tap the bookmark on a post to read it later."
                      : forYou
                        ? 'No posts matching your interests yet. Update your Forum Topics of Interest in your profile!'
                        : activeTopic
                          ? `No posts in #${activeTopic} yet`
                          : 'No posts yet. Start the conversation!'}
                  </p>
                  {!viewUserParam && !forYou && !savedMode && (
                    <Button variant="outline" onClick={() => setShowCreate(true)}>Create First Post</Button>
                  )}
                  {forYou && (
                    <Button variant="outline" onClick={() => navigate('/profile')}>Update My Interests</Button>
                  )}
                  {savedMode && (
                    <Button variant="outline" onClick={() => setSavedMode(false)} data-testid="saved-empty-browse">
                      Browse All Posts
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="space-y-3" data-testid="forum-feed">
                  {displayPosts.map((post, idx) => {
                    // After every Nth post, slot in a sponsored banner from
                    // the rotation. Skips when no feed banners are
                    // configured. Done as inline JSX rather than a sub-
                    // component to keep the surrounding map signature
                    // unchanged.
                    const FEED_BANNER_INTERVAL = 10;
                    const showBanner = feedBanners.length > 0
                      && idx > 0
                      && (idx + 1) % FEED_BANNER_INTERVAL === 0;
                    const bannerForSlot = showBanner
                      ? feedBanners[Math.floor(idx / FEED_BANNER_INTERVAL) % feedBanners.length]
                      : null;
                    return (
                      <React.Fragment key={post.post_id}>
                        <PostCard
                          post={post}
                          onReact={handleReact}
                          onOpenPost={handleOpenPost}
                          onAuthorClick={handleAuthorClick}
                          onMentionUserClick={handleMentionUserClick}
                          onMentionCompanyClick={handleMentionCompanyClick}
                          onRequestBriteLab={handleRequestBriteLab}
                          feedbackPromptId={feedbackPromptId}
                          onFeedback={handleFeedback}
                          isAdmin={user?.role === 'admin'}
                          onTogglePin={handleTogglePin}
                          onDelete={handleCardDeletePost}
                          onToggleSave={handleToggleSave}
                          currentUserId={user?.user_id}
                        />
                        {bannerForSlot && (
                          <Card
                            className="p-3 border-amber-200/40 bg-amber-50/30"
                            data-testid={`feed-banner-${bannerForSlot.banner_id}`}
                          >
                            <p className="text-[10px] text-slate-400 mb-1.5 text-center uppercase tracking-wide">Sponsored</p>
                            {bannerForSlot.click_url ? (
                              <a
                                href={bannerForSlot.click_url}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                onClick={() => {
                                  try {
                                    fetch(`${BACKEND_URL}/api/banners/${bannerForSlot.banner_id}/click`, {
                                      method: 'POST', credentials: 'include', keepalive: true,
                                    }).catch(() => {});
                                  } catch {}
                                }}
                                className="block"
                              >
                                <img
                                  src={bannerForSlot.image_url}
                                  alt="Sponsor"
                                  className="w-full max-h-[200px] object-contain rounded mx-auto"
                                  loading="lazy"
                                />
                              </a>
                            ) : (
                              <img
                                src={bannerForSlot.image_url}
                                alt="Sponsor"
                                className="w-full max-h-[200px] object-contain rounded mx-auto"
                                loading="lazy"
                              />
                            )}
                          </Card>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {!viewUserParam && !savedMode && totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6" data-testid="forum-pagination">
                  <Button variant="outline" size="sm" disabled={page <= 1}
                    onClick={() => { setPage(page - 1); loadFeed(page - 1, activeTopic, forYou); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="prev-page">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages}
                    onClick={() => { setPage(page + 1); loadFeed(page + 1, activeTopic, forYou); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="next-page">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Archive link — shown on the final page of the main feed
                  when there's more content beyond the 3-page cap. */}
              {!viewUserParam && !savedMode && !isArchive
                && page === totalPages
                && totalPosts > MAIN_FEED_PAGE_CAP * 10 && (
                <div className="flex justify-center mt-6" data-testid="view-archive-cta">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/forum/archive')}
                    className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy/5"
                    data-testid="view-archive-btn"
                  >
                    View Archive ({totalPosts - MAIN_FEED_PAGE_CAP * 10}+ older posts) →
                  </Button>
                </div>
              )}

              {/* Back-to-main link on the archive page */}
              {isArchive && (
                <div className="flex justify-center mt-6" data-testid="back-to-feed-cta">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/forum')}
                    className="text-brand-navy hover:bg-brand-navy/5"
                    data-testid="back-to-feed-btn"
                  >
                    ← Back to main feed
                  </Button>
                </div>
              )}
            </div>

            {/* Right Sidebar — first card is aligned with the top of the
                first post via lg:mt-* (approximates header + tabs +
                filters block height on desktop). */}
            <div className="w-full lg:w-72 flex-shrink-0 space-y-4 lg:mt-[232px]">
              <SponsorBanner placement="forum" type="side" />
              <LatestNewsPanel testid="forum-latest-news" />
              <MostActiveBriteLabsCard />
              <PeopleToFollowCard currentUserId={user?.user_id} />
              <SponsorBanner placement="forum-2" type="side" />

              {/* Blocked Users */}
              {blockedUsers.length > 0 && (
                <Card className="p-4" data-testid="blocked-sidebar">
                  <button onClick={() => setShowBlocked(!showBlocked)} className="flex items-center justify-between w-full">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Blocked ({blockedUsers.length})</h3>
                    <ShieldBan className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  {showBlocked && (
                    <div className="mt-2 space-y-2">
                      {blockedUsers.map(b => (
                        <div key={b.blocked_user_id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-600 truncate">{b.name}</span>
                          <Button variant="ghost" size="sm" className="text-xs text-brand-navy h-6 px-2"
                            onClick={() => handleUnblock(b.blocked_user_id)} data-testid={`unblock-${b.blocked_user_id}`}>
                            <ShieldCheck className="h-3 w-3 mr-1" /> Unblock
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>
          </div>
          <QuickActions user={user} exclude={['/forum']} />
        </div>
      </main>

      {/* Create Post Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-post-dialog">
          <DialogHeader>
            <DialogTitle>New Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Title (optional)" value={newPost.title}
              onChange={e => setNewPost(p => ({ ...p, title: e.target.value }))} data-testid="post-title-input" />
            <MentionRichTextEditor placeholder="Share your thoughts, ask a question, or start a discussion..."
              value={newPost.content} onChange={val => setNewPost(p => ({ ...p, content: val }))}
              testId="post-content-input" minHeight="8rem" />
            <WordCountLine text={newPost.content} testId="post-content-word-count" />
            <div>
              <p className="text-xs text-slate-500 mb-2">Topics (up to 5):</p>
              <MultiSelectTopics
                topics={topics}
                selected={newPost.topics}
                onChange={(next) => setNewPost(p => ({ ...p, topics: next }))}
                max={5}
                placeholder="Pick one or more topics…"
                testId="topic-selector"
              />
            </div>

            {/* Link Preview */}
            <div data-testid="link-preview-block">
              <p className="text-xs text-slate-500 mb-2">Add a link (article, video, partner page) — we'll auto-pull the title, description and image:</p>
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/article"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchLinkPreview(); } }}
                  data-testid="link-url-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={fetchLinkPreview} disabled={!linkUrl.trim() || fetchingPreview} data-testid="fetch-link-preview-btn">
                  {fetchingPreview ? 'Fetching…' : 'Fetch preview'}
                </Button>
              </div>
              {linkPreview && (
                <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="link-preview-card">
                  <div className="flex items-stretch">
                    {(customLinkImage?.url || linkPreview.image) ? (
                      <img src={customLinkImage ? `${BACKEND_URL}${customLinkImage.url}` : linkPreview.image} alt="" className="w-32 h-24 object-cover flex-shrink-0 bg-slate-100" />
                    ) : (
                      <div className="w-32 h-24 bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Image className="h-6 w-6 text-slate-300" />
                      </div>
                    )}
                    <div className="p-3 flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{linkPreview.site || new URL(linkPreview.url || linkUrl).hostname}</p>
                      <p className="text-sm font-semibold text-slate-800 line-clamp-2">{linkPreview.title || linkUrl}</p>
                      {linkPreview.description && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{linkPreview.description}</p>}
                    </div>
                    <button onClick={() => { setLinkPreview(null); setCustomLinkImage(null); }} className="p-2 text-slate-400 hover:text-rose-500 self-start" data-testid="remove-link-preview" title="Remove link preview">
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="border-t border-slate-100 px-3 py-2 flex items-center gap-2 bg-slate-50">
                    <span className="text-[10px] text-slate-500">Image:</span>
                    {(customLinkImage?.url || linkPreview.image) ? (
                      <span className="text-[10px] text-slate-600">{customLinkImage ? 'Custom upload' : 'From the website'}</span>
                    ) : (
                      <span className="text-[10px] text-rose-500">No image found — upload one</span>
                    )}
                    <label className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] border border-slate-200 rounded cursor-pointer hover:bg-white transition-colors">
                      <Image className="h-3 w-3 text-slate-500" />
                      <span className="text-slate-600">{customLinkImage ? 'Replace image' : 'Upload image'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleLinkImageUpload} data-testid="link-image-upload" />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Image & File Attachments */}
            <div>
              <p className="text-xs text-slate-500 mb-1" data-testid="upload-hint-line1">
                Add an image or attach files (images, PDFs, docs).
              </p>
              <p className="text-[11px] text-slate-400 mb-2" data-testid="upload-hint-line2">
                <span className="font-medium text-slate-500">Recommended image:</span> 1200 × 800 px (landscape, 3:2)
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-medium text-slate-500">Max image size:</span> 1 MB
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-medium text-slate-500">Max file size (PDF/doc):</span> 10 MB
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-medium text-slate-500">Formats:</span> JPG, PNG, WebP, GIF
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 px-3 py-2 text-xs border border-brand-navy/30 bg-brand-navy/5 rounded-lg cursor-pointer hover:bg-brand-navy/10 transition-colors text-brand-navy font-medium" data-testid="add-image-btn">
                  <Image className="h-3.5 w-3.5" />
                  <span>{uploading ? 'Uploading…' : 'Add Image'}</span>
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handleFileUpload} data-testid="add-image-input" />
                </label>
                <label className="flex items-center gap-2 px-3 py-2 text-xs border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors" data-testid="attach-file-btn">
                  <Paperclip className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-slate-600">{uploading ? 'Uploading…' : 'Attach Files'}</span>
                  <input type="file" multiple className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" />
                </label>
                {attachments.length > 0 && <span className="text-xs text-slate-400">{attachments.length} file(s)</span>}
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {attachments.map((att, i) => (
                    <div key={att.file_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-xs" data-testid={`attachment-${att.file_id}`}>
                      {att.is_image ? (
                        <img src={`${BACKEND_URL}${att.url}`} alt={att.filename} className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate text-slate-700">{att.filename}</span>
                      <span className="text-slate-400">{(att.size / 1024).toFixed(0)}KB</span>
                      <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={handleCreatePost} disabled={creating || countWords(newPost.content) > POST_WORD_LIMIT} data-testid="submit-post-btn"
              className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">
              {creating ? 'Publishing...' : 'Publish Post'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post Detail Dialog */}
      <Dialog open={!!selectedPost || detailLoading} onOpenChange={() => {
        setSelectedPost(null);
        setCommentText('');
        // Strip the ?post= deep-link param so refresh / back-button doesn't re-open.
        if (postParam) {
          const next = new URLSearchParams(searchParams);
          next.delete('post');
          setSearchParams(next, { replace: true });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="post-detail-dialog">
          {detailLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
            </div>
          ) : selectedPost && (
            <>
              <div className="flex items-start gap-3 mb-4">
                <button onClick={() => { setSelectedPost(null); handleAuthorClick(selectedPost.author_id); }}>
                  <AuthorAvatar name={selectedPost.author_name} picture={selectedPost.author_picture} size="lg" />
                </button>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => { setSelectedPost(null); handleAuthorClick(selectedPost.author_id); }}
                      className="font-semibold text-slate-800 hover:text-brand-navy">{selectedPost.author_name}</button>
                    {selectedPost.author_role === 'admin' && <Badge variant="outline" className="text-[10px]">Admin</Badge>}
                    <span className="text-xs text-slate-400">{timeAgo(selectedPost.created_at)}</span>
                  </div>
                  {selectedPost.title && <h2 className="text-lg font-bold text-slate-900 mt-1">{selectedPost.title}</h2>}
                </div>
                <div className="flex items-center gap-1">
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-brand-navy h-8 w-8 p-0"
                          onClick={() => sharePostLink(selectedPost.post_id)}
                          data-testid="share-post-btn">
                          <LinkIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent><p>Copy link to post</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {selectedPost.author_id !== user?.user_id && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                            onClick={() => setReportTarget({ userId: selectedPost.author_id, userName: selectedPost.author_name, contextType: 'forum_post', contextId: selectedPost.post_id })}
                            data-testid="detail-report-btn">
                            <Flag className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent><p>Report Member</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {selectedPost.author_id !== user?.user_id && !isBlocked(selectedPost.author_id) && (
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                      onClick={() => handleBlock(selectedPost.author_id, selectedPost.author_name)} data-testid="detail-block-btn">
                      <ShieldBan className="h-4 w-4" />
                    </Button>
                  )}
                  {(selectedPost.author_id === user?.user_id || user?.role === 'admin') && (
                    <>
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-brand-navy h-8 w-8 p-0"
                        onClick={() => { setEditingPost(selectedPost); setEditForm({ title: selectedPost.title || '', content: selectedPost.content || '' }); }}
                        data-testid="edit-post-btn">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                        onClick={() => handleDeletePost(selectedPost.post_id)} data-testid="delete-post-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {editingPost?.post_id === selectedPost.post_id ? (
                <div className="space-y-3" data-testid="edit-post-form">
                  <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder="Post title (optional)" data-testid="edit-post-title" />
                  <MentionRichTextEditor value={editForm.content} onChange={val => setEditForm({ ...editForm, content: val })}
                    testId="edit-post-content" minHeight="6rem" />
                  <WordCountLine text={editForm.content} testId="edit-post-word-count" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleEditPost} disabled={countWords(editForm.content) > POST_WORD_LIMIT} className="bg-brand-navy text-white" data-testid="save-edit-btn">Save Changes</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingPost(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <PostBody content={selectedPost.content} onUserClick={handleMentionUserClick} onCompanyClick={handleMentionCompanyClick} className="text-sm text-slate-700" />
                  {!selectedPost.link_preview?.url && <LinkPreviews text={selectedPost.content} />}
                </>
              )}
              {selectedPost.edited_at && !editingPost && (
                <span className="text-[10px] text-slate-400 italic">(edited {timeAgo(selectedPost.edited_at)})</span>
              )}

              {/* Attachments */}
              {selectedPost.attachments?.length > 0 && (
                <div className="mt-5 space-y-3" data-testid="post-attachments">
                  {(() => {
                    const imgs = selectedPost.attachments.filter(a => a.is_image);
                    if (imgs.length === 0) return null;
                    if (imgs.length === 1) {
                      const att = imgs[0];
                      return (
                        <a href={`${BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                          className="block rounded-xl overflow-hidden border border-slate-200 hover:shadow-md transition-shadow">
                          <img src={`${BACKEND_URL}${att.url}`} alt={att.filename}
                            className="w-full max-h-[600px] object-contain bg-white"
                            data-testid={`img-preview-${att.file_id}`} />
                        </a>
                      );
                    }
                    return (
                      <div className={`grid gap-2 ${imgs.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
                        {imgs.map(att => (
                          <a key={att.file_id} href={`${BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                            className="block rounded-lg overflow-hidden border border-slate-200 hover:shadow-md transition-shadow">
                            <img src={`${BACKEND_URL}${att.url}`} alt={att.filename}
                              className="w-full h-56 object-cover" data-testid={`img-preview-${att.file_id}`} />
                          </a>
                        ))}
                      </div>
                    );
                  })()}
                  {selectedPost.attachments.filter(a => !a.is_image).length > 0 && (
                    <div className="space-y-1.5">
                      {selectedPost.attachments.filter(a => !a.is_image).map(att => (
                        <a key={att.file_id} href={`${BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg text-sm hover:bg-slate-100 transition-colors group" data-testid={`file-attachment-${att.file_id}`}>
                          <FileText className="h-4 w-4 text-brand-navy/50 flex-shrink-0" />
                          <span className="flex-1 truncate text-slate-700 group-hover:text-brand-navy">{att.filename}</span>
                          <span className="text-xs text-slate-400">{(att.size / 1024).toFixed(0)}KB</span>
                          <Download className="h-3.5 w-3.5 text-slate-400 group-hover:text-brand-navy" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedPost.topics?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {selectedPost.topics.map(t => (
                    <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-navy/8 text-brand-navy/80 font-medium">#{t}</span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-4 mt-4 pt-3 border-t">
                <div className="flex items-center gap-1">
                  {[
                    { type: 'like', emoji: '\uD83D\uDC4D', label: 'Like' },
                    { type: 'love', emoji: '\u2764\uFE0F', label: 'Love' },
                    { type: 'celebrate', emoji: '\uD83C\uDF89', label: 'Celebrate' },
                    { type: 'insightful', emoji: '\uD83D\uDCA1', label: 'Insightful' },
                    { type: 'curious', emoji: '\uD83E\uDD14', label: 'Curious' },
                  ].map(r => (
                    <button key={r.type} onClick={() => handleReact(selectedPost.post_id, r.type)}
                      title={r.label} data-testid={`detail-react-${r.type}`}
                      className={`text-lg hover:scale-110 transition-transform px-0.5 ${selectedPost.my_reaction === r.type ? 'scale-125 bg-brand-navy/10 rounded-full' : 'opacity-60 hover:opacity-100'}`}>
                      {r.emoji}
                    </button>
                  ))}
                  <span className="text-xs text-slate-400 ml-1">{Object.values(selectedPost.reaction_counts || {}).reduce((a, b) => a + b, 0) || selectedPost.like_count || 0}</span>
                </div>
                <span className="text-sm text-slate-400 flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4" /> {selectedPost.comments?.length || 0} comments
                </span>
                <button onClick={() => handleToggleSave(selectedPost)}
                  data-testid={`detail-save-btn-${selectedPost.post_id}`}
                  title={selectedPost.saved_by_me ? 'Remove from saved' : 'Save for later'}
                  className={`ml-auto flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    selectedPost.saved_by_me ? 'text-amber-600' : 'text-slate-400 hover:text-amber-600'
                  }`}>
                  <Bookmark className={`h-4 w-4 ${selectedPost.saved_by_me ? 'fill-amber-500' : ''}`} />
                  {selectedPost.saved_by_me ? 'Saved' : 'Save'}
                </button>
              </div>

              {/* Feedback prompt in detail view */}
              {selectedPost.my_feedback === null && feedbackPromptId === selectedPost.post_id && (
                <div className="mt-3 p-3 bg-brand-navy/5 rounded-lg flex items-center gap-3" data-testid="detail-feedback-prompt">
                  <Sparkles className="h-4 w-4 text-brand-navy" />
                  <span className="text-xs text-slate-600 flex-1">Did this post add value for you?</span>
                  <button onClick={() => handleFeedback(selectedPost.post_id, true)}
                    className="p-1.5 rounded-md hover:bg-green-100 text-green-600"><ThumbsUp className="h-4 w-4" /></button>
                  <button onClick={() => handleFeedback(selectedPost.post_id, false)}
                    className="p-1.5 rounded-md hover:bg-red-100 text-red-400"><ThumbsDown className="h-4 w-4" /></button>
                </div>
              )}

              {/* Tabs: Comments & Breakout Groups */}
              <Tabs value={detailTab} onValueChange={setDetailTab} className="mt-5">
                <TabsList className="w-full grid grid-cols-2 h-9">
                  <TabsTrigger value="comments" className="text-xs gap-1.5" data-testid="detail-comments-tab">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Comments {selectedPost.comments?.length > 0 && `(${selectedPost.comments.length})`}
                  </TabsTrigger>
                  <TabsTrigger value="breakout" className="text-xs gap-1.5" data-testid="detail-breakout-tab">
                    <Users className="h-3.5 w-3.5" />
                    Breakout Groups {breakoutGroups.length > 0 && `(${breakoutGroups.length})`}
                  </TabsTrigger>
                </TabsList>

                {/* Comments Tab */}
                <TabsContent value="comments" className="mt-3 space-y-3" data-testid="comments-section">
                  {(() => {
                    if (!selectedPost.comments?.length) return (
                      <p className="text-xs text-slate-400 py-2">No comments yet. Be the first to reply.</p>
                    );
                    // Build top-level + replies thread
                    const topLevel = selectedPost.comments.filter(c => !c.parent_comment_id);
                    const repliesByParent = {};
                    selectedPost.comments.filter(c => c.parent_comment_id).forEach(r => {
                      (repliesByParent[r.parent_comment_id] = repliesByParent[r.parent_comment_id] || []).push(r);
                    });

                    const renderComment = (c, isReply = false) => {
                      const totalReactions = Object.values(c.reactions || {}).reduce((a, b) => a + b, 0);
                      const myReactionEmoji = c.my_reaction ? REACTION_TYPES.find(r => r.type === c.my_reaction)?.emoji : null;
                      const isMine = c.author_id === user?.user_id;
                      const isEditing = editingCommentId === c.comment_id;
                      return (
                        <div key={c.comment_id} className={`${isReply ? 'ml-10' : ''}`}>
                          <div className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-lg" data-testid={`comment-${c.comment_id}`}>
                            <button onClick={() => { setSelectedPost(null); handleAuthorClick(c.author_id); }}>
                              <AuthorAvatar name={c.author_name} picture={c.author_picture} size="sm" />
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => { setSelectedPost(null); handleAuthorClick(c.author_id); }}
                                  className="text-xs font-semibold text-slate-700 hover:text-brand-navy transition-colors"
                                  data-testid={`comment-author-name-${c.comment_id}`}
                                >{c.author_name}</button>
                                <span className="text-[10px] text-slate-400">{timeAgo(c.created_at)}{c.edited_at ? ' · edited' : ''}</span>
                              </div>
                              {isEditing ? (
                                <div className="mt-1.5">
                                  <MentionRichTextEditor value={editingCommentText} onChange={setEditingCommentText}
                                    testId={`edit-comment-input-${c.comment_id}`} minHeight="4rem" />
                                  <div className="flex gap-1.5 mt-1.5">
                                    <Button size="sm" className="bg-brand-navy text-white h-7 text-xs px-2.5"
                                      data-testid={`save-edit-comment-${c.comment_id}`}
                                      onClick={() => saveEditComment(c.comment_id)}>
                                      <Check className="h-3 w-3 mr-1" /> Save
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                                      onClick={() => { setEditingCommentId(null); setEditingCommentText(''); }}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <PostBody content={c.content} onUserClick={handleMentionUserClick} onCompanyClick={handleMentionCompanyClick} className="text-sm text-slate-600 mt-0.5" />
                                  <LinkPreviews text={c.content} />
                                </>
                              )}
                              {c.attachments?.length > 0 && !isEditing && (
                                <div className="flex gap-2 flex-wrap mt-2">
                                  {c.attachments.filter(a => a.is_image).map((att, i) => (
                                    <a key={`img-${i}`} href={att.url} target="_blank" rel="noopener noreferrer">
                                      <img src={att.url} alt={att.filename}
                                        className="h-24 w-24 rounded-md object-cover border border-slate-200 hover:opacity-90 transition-opacity" />
                                    </a>
                                  ))}
                                  {c.attachments.filter(a => !a.is_image).map((att, i) => (
                                    <a key={`file-${i}`} href={att.url} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-slate-50 border border-slate-200 hover:border-brand-navy/40 transition-colors">
                                      <FileText className="h-3 w-3" /> {att.filename}
                                    </a>
                                  ))}
                                </div>
                              )}

                              {/* Action row: react, reply, total reactions */}
                              {!isEditing && (
                                <div className="flex items-center gap-2 mt-1.5 relative">
                                  <button type="button"
                                    onClick={() => setOpenCommentReactions(openCommentReactions === c.comment_id ? null : c.comment_id)}
                                    data-testid={`react-comment-${c.comment_id}`}
                                    className={`text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${
                                      myReactionEmoji ? 'text-brand-navy font-semibold bg-brand-navy/5' : 'text-slate-400 hover:text-brand-navy hover:bg-brand-navy/5'
                                    }`}>
                                    {myReactionEmoji ? <span>{myReactionEmoji}</span> : <Heart className="h-3 w-3" />}
                                    React
                                  </button>
                                  {openCommentReactions === c.comment_id && (
                                    <div className="absolute -top-9 left-0 bg-white border border-slate-200 rounded-full px-2 py-1 flex gap-1 shadow-md z-10"
                                      data-testid={`comment-reactions-popup-${c.comment_id}`}>
                                      {REACTION_TYPES.map(r => (
                                        <button key={r.type} type="button" onClick={() => reactToComment(c.comment_id, r.type)}
                                          data-testid={`react-comment-pick-${c.comment_id}-${r.type}`}
                                          title={r.label}
                                          className="text-lg hover:scale-125 transition-transform">
                                          {r.emoji}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {!isReply && (
                                    <button type="button" onClick={() => startReplyToComment(c)}
                                      data-testid={`reply-comment-${c.comment_id}`}
                                      className="text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-slate-400 hover:text-brand-navy hover:bg-brand-navy/5 transition-colors">
                                      <Reply className="h-3 w-3" /> Reply
                                    </button>
                                  )}
                                  {totalReactions > 0 && (
                                    <div className="text-[11px] text-slate-500 flex items-center gap-0.5">
                                      {Object.entries(c.reactions || {}).slice(0, 3).map(([type, n]) => (
                                        <span key={type}>{REACTION_TYPES.find(r => r.type === type)?.emoji}</span>
                                      ))}
                                      <span>{totalReactions}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {(isMine || user?.role === 'admin') && !isEditing && (
                              <div className="flex flex-col gap-0.5">
                                {isMine && (
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-300 hover:text-brand-navy"
                                    onClick={() => startEditComment(c)} data-testid={`edit-comment-${c.comment_id}`}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-300 hover:text-red-500"
                                  onClick={() => handleDeleteComment(c.comment_id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Inline reply composer — MentionInput so members
                              can @-tag one another (previously a plain
                              textarea, which is why replies to comments
                              couldn't use @). */}
                          {replyToCommentId === c.comment_id && (
                            <div className="ml-10 mt-1.5 flex items-start gap-2" data-testid={`reply-composer-${c.comment_id}`}>
                              <div className="flex-1 min-w-0">
                                <MentionRichTextEditor
                                  value={replyText}
                                  onChange={setReplyText}
                                  placeholder={`Reply to ${c.author_name}…`}
                                  testId={`reply-input-${c.comment_id}`}
                                  minHeight="4rem"
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button size="sm" className="bg-brand-navy text-white h-7 text-xs px-2.5"
                                  data-testid={`send-reply-${c.comment_id}`}
                                  onClick={() => submitReply(c.comment_id)} disabled={!replyText.trim()}>
                                  Reply
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                                  onClick={() => { setReplyToCommentId(null); setReplyText(''); }}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Render replies */}
                          {!isReply && repliesByParent[c.comment_id]?.length > 0 && (
                            <div className="space-y-2 mt-2">
                              {repliesByParent[c.comment_id].map(r => renderComment(r, true))}
                            </div>
                          )}
                        </div>
                      );
                    };

                    return topLevel.map(c => renderComment(c, false));
                  })()}

                  <div className="pt-2 space-y-2">
                    <MentionRichTextEditor
                      value={commentText}
                      onChange={setCommentText}
                      placeholder="Write a comment... Use @ to mention users"
                      disabled={submittingComment}
                      testId="comment-input"
                      minHeight="5rem"
                    />
                    {commentAttachments.length > 0 && (
                      <div className="flex gap-2 flex-wrap" data-testid="comment-attachment-previews">
                        {commentAttachments.map((att, i) => (
                          <div key={i} className="relative group">
                            {att.is_image ? (
                              <img src={att.url} alt={att.filename}
                                className="h-16 w-16 rounded-md object-cover border border-slate-200" />
                            ) : (
                              <div className="h-16 w-16 rounded-md border border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-[9px] text-slate-500 px-1 text-center">
                                <FileText className="h-4 w-4 mb-0.5" />
                                <span className="truncate w-full">{att.filename}</span>
                              </div>
                            )}
                            <button type="button"
                              onClick={() => setCommentAttachments(prev => prev.filter((_, idx) => idx !== i))}
                              className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`remove-comment-attachment-${i}`}>
                              <XIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <input ref={commentImageInputRef} type="file" accept="image/*" multiple className="hidden"
                      onChange={handleCommentImageUpload} data-testid="comment-image-file-input" />
                    <div className="flex justify-between items-center">
                      <Button size="sm" variant="ghost" onClick={() => commentImageInputRef.current?.click()}
                        disabled={uploadingComment} data-testid="comment-add-image-btn">
                        <Image className="h-4 w-4 mr-1.5" /> {uploadingComment ? 'Uploading…' : 'Add image'}
                      </Button>
                      <Button size="sm" onClick={handleComment}
                        disabled={submittingComment || (!commentText.trim() && commentAttachments.length === 0)}
                        data-testid="submit-comment-btn" className="bg-brand-navy text-white">
                        <Send className="h-4 w-4 mr-1.5" /> Comment
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Breakout Groups Tab */}
                <TabsContent value="breakout" className="mt-3" data-testid="breakout-groups-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                      <Users className="h-4 w-4" /> Breakout Groups
                      {breakoutGroups.length > 0 && <span className="text-xs text-slate-400 font-normal">({breakoutGroups.length})</span>}
                    </h3>
                    <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="create-breakout-btn"
                      onClick={() => setShowCreateGroup(!showCreateGroup)}>
                      <Plus className="h-3 w-3 mr-1" /> New Group
                    </Button>
                  </div>

                  {showCreateGroup && (
                    <div className="p-3 bg-slate-50 border rounded-lg mb-3 space-y-2" data-testid="create-breakout-form">
                      <Input placeholder="Group name (e.g. Scope 3 Reporting)" value={newGroupForm.name}
                        onChange={e => setNewGroupForm(f => ({ ...f, name: e.target.value }))}
                        className="h-8 text-sm" data-testid="breakout-group-name" />
                      <Input placeholder="Brief description (optional)" value={newGroupForm.description}
                        onChange={e => setNewGroupForm(f => ({ ...f, description: e.target.value }))}
                        className="h-8 text-sm" data-testid="breakout-group-desc" />
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs bg-brand-navy text-white" onClick={handleCreateGroup}
                          disabled={!newGroupForm.name.trim()} data-testid="submit-breakout-btn">Create</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowCreateGroup(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}

                  {activeGroup ? (
                    <div className="border rounded-lg overflow-hidden" data-testid={`breakout-chat-${activeGroup.group_id}`}>
                      <div className="flex items-center justify-between bg-brand-navy/5 px-3 py-2 border-b">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-sm font-semibold text-brand-navy truncate" data-testid={`breakout-name-${activeGroup.group_id}`}>{activeGroup.name}</span>
                          {(activeGroup.created_by === user?.user_id || user?.role === 'admin') && (
                            <button type="button" onClick={() => handleRenameGroup(activeGroup.group_id)}
                              data-testid={`rename-breakout-${activeGroup.group_id}`}
                              className="text-slate-400 hover:text-brand-navy p-0.5 rounded transition-colors"
                              title="Rename group">
                              <Pencil className="h-3 w-3" />
                            </button>
                          )}
                          <span className="text-xs text-slate-400 ml-2 whitespace-nowrap">{activeGroup.member_count} member{activeGroup.member_count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {activeGroup.is_member && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (await window.appConfirm(`Leave "${activeGroup.name}"? You'll stop receiving group updates and your messages will remain in the chat.`)) {
                                  handleLeaveGroup(activeGroup.group_id);
                                }
                              }}
                              data-testid={`leave-breakout-${activeGroup.group_id}`}
                              className="text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-0.5 rounded transition-colors">
                              Leave group
                            </button>
                          )}
                          <button onClick={() => { setActiveGroup(null); setGroupMessages([]); }} className="text-slate-400 hover:text-slate-600">
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto p-3 space-y-2 bg-white">
                        {groupMessages.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-4">No messages yet. Start the conversation!</p>
                        ) : groupMessages.map(m => (
                          <div key={m.message_id} className="flex gap-2" data-testid={`breakout-msg-${m.message_id}`}>
                            <button
                              type="button"
                              onClick={() => { setSelectedPost(null); handleAuthorClick(m.author_id); }}
                              className="h-6 w-6 rounded-full bg-brand-navy/10 flex items-center justify-center flex-shrink-0 hover:bg-brand-navy/20 transition-colors"
                              data-testid={`breakout-msg-avatar-${m.message_id}`}
                              disabled={!m.author_id}
                            >
                              <span className="text-[10px] font-bold text-brand-navy">{(m.author_name || '?')[0]}</span>
                            </button>
                            <div>
                              <button
                                type="button"
                                onClick={() => { setSelectedPost(null); handleAuthorClick(m.author_id); }}
                                className="text-xs font-semibold text-slate-700 hover:text-brand-navy transition-colors"
                                data-testid={`breakout-msg-author-${m.message_id}`}
                                disabled={!m.author_id}
                              >{m.author_name}</button>
                              <span className="text-[10px] text-slate-400 ml-1.5">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              <div className="text-xs text-slate-600 mt-0.5 break-words [overflow-wrap:anywhere]">
                                <PostBody content={m.content} onUserClick={handleMentionUserClick} onCompanyClick={handleMentionCompanyClick} className="compact" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-2 border-t bg-slate-50 space-y-2">
                        <MentionRichTextEditor
                          value={groupMsgText}
                          onChange={setGroupMsgText}
                          placeholder="Type a message... @ to mention"
                          testId="breakout-msg-input"
                          minHeight="5rem"
                        />
                        <div className="flex justify-end">
                          <Button size="sm" onClick={handleSendGroupMsg}
                            disabled={!groupMsgText.trim()} data-testid="breakout-send-btn">
                            <Send className="h-3.5 w-3.5 mr-1.5" /> Send
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {loadingGroups ? (
                        <div className="flex justify-center py-3"><div className="animate-spin h-5 w-5 border-2 border-brand-navy border-t-transparent rounded-full" /></div>
                      ) : breakoutGroups.length === 0 ? (
                        <p className="text-xs text-slate-400 py-2">No breakout groups yet. Create one to start a focused side-conversation.</p>
                      ) : breakoutGroups.map(g => (
                        <div key={g.group_id} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-slate-50 transition-colors"
                          data-testid={`breakout-group-${g.group_id}`}>
                          <div className="h-8 w-8 rounded-full bg-brand-navy/10 flex items-center justify-center flex-shrink-0">
                            <Users className="h-4 w-4 text-brand-navy" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{g.name}</p>
                            <p className="text-xs text-slate-400">{g.member_count} member{g.member_count !== 1 ? 's' : ''} · {g.message_count} message{g.message_count !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {g.is_member ? (
                              <>
                                <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={`open-group-${g.group_id}`}
                                  onClick={() => openGroupChat(g)}>Open</Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" data-testid={`leave-group-${g.group_id}`}
                                  onClick={() => handleLeaveGroup(g.group_id)}>Leave</Button>
                              </>
                            ) : (
                              <Button size="sm" className="h-7 text-xs bg-brand-navy text-white" data-testid={`join-group-${g.group_id}`}
                                onClick={() => handleJoinGroup(g.group_id)}>Join</Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Member Dialog */}
      <ReportDialog
        open={!!reportTarget}
        onOpenChange={() => setReportTarget(null)}
        reportedUserId={reportTarget?.userId}
        reportedUserName={reportTarget?.userName}
        contextType={reportTarget?.contextType}
        contextId={reportTarget?.contextId}
      />

      {/* Pin Forum Post Dialog */}
      <PinUntilDialog
        open={!!pinTarget}
        onOpenChange={(v) => { if (!v) setPinTarget(null); }}
        onConfirm={submitPin}
        postTitle={pinTarget?.title}
      />
    </div>
  );
}
